import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import AlertCard from '../components/AlertCard';
import CreateAlertModal from '../components/CreateAlertModal';
import { Button } from '../components/ui/button';
import { Plus, Bell, Loader2, AlertCircle } from 'lucide-react';
import { alertsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

const AlertsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Removed authentication requirement - alerts accessible to all
  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const data = await alertsAPI.getAll();
      setAlerts(data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load alerts',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewResults = (alert) => {
    // Navigate to alert detail page
    navigate(`/alerts/${alert._id}`);
  };

  // Removed authentication check - page always renders

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
              <Bell className="w-8 h-8 text-red-500" />
              <span>My Alerts</span>
            </h1>
            <p className="text-gray-600 mt-2">
              Get notified when new items matching your criteria appear on marketplace sites
            </p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-red-500 hover:bg-red-600 text-white"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Alert
          </Button>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900">How it works</h3>
              <p className="text-sm text-blue-800 mt-1">
                Create alerts for products you want to buy. Our system will automatically monitor marketplace sites every hour and notify you when new listings match your criteria. Alerts expire after 30 days.
              </p>
            </div>
          </div>
        </div>

        {/* Alerts Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-red-500" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Bell className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">No alerts yet</h2>
            <p className="text-gray-600 mb-6">
              Create your first alert to start monitoring marketplace sites for products you want
            </p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Your First Alert
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {alerts.map((alert) => (
              <AlertCard
                key={alert.id || alert._id}
                alert={alert}
                onUpdate={fetchAlerts}
                onDelete={fetchAlerts}
                onViewResults={handleViewResults}
              />
            ))}
          </div>
        )}

        {/* Stats */}
        {alerts.length > 0 && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-gray-600 text-sm">Total Alerts</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{alerts.length}</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-gray-600 text-sm">Active Alerts</p>
              <p className="text-3xl font-bold text-green-600 mt-1">
                {alerts.filter((a) => a.status === 'active' || a.status === 'ready').length}
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-gray-600 text-sm">Paused Alerts</p>
              <p className="text-3xl font-bold text-gray-600 mt-1">
                {alerts.filter((a) => a.status === 'paused').length}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateAlertModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchAlerts}
      />
    </div>
  );
};

export default AlertsPage;
